import { useContext, useMemo } from "react";
import { useVictimsAndKillers } from "../useVictimsAndKillers";
import { CustomMarkersContext } from "../../context/CustomMarkersContext";

export type MarkerPoint = {
  position: [number, number];
  color?: string;
  kind?: "missing" | "murder" | "unsolved" | "survived" | "custom";
  label?: string;
  payload?: any;
  rowId: number;
};

function kindFromStatus(
  status: string | null | undefined
): "missing" | "murder" | "unsolved" | "survived" | undefined {
  if (!status) return undefined;
  const s = status.toLowerCase();
  if (s.startsWith("missing")) return "missing";
  if (s.startsWith("murder")) return "murder";
  if (s.startsWith("unsolved")) return "unsolved";
  if (s.startsWith("survived")) return "survived";
  return undefined;
}

function victimLabel(v: any): string | undefined {
  return (
    v?.name ??
    v?.fullName ??
    (v?.firstName || v?.lastName
      ? [v.firstName, v.lastName].filter(Boolean).join(" ")
      : undefined)
  );
}

export function useMarkers(): MarkerPoint[] {
  const ctx = useVictimsAndKillers() as any;
  const customCtx = useContext(CustomMarkersContext);

  return useMemo<MarkerPoint[]>(() => {
    const rows: any[] = Array.isArray(ctx.filters)
      ? ctx.filters
      : Array.isArray(ctx.rows)
      ? ctx.rows
      : [];

    const victimsMap: Record<number, any[]> =
      (ctx.victimsByFilter as Record<number, any[]>) ??
      (ctx.victimsByRow as Record<number, any[]>) ??
      {};

    const out: MarkerPoint[] = [];
    for (const row of rows) {
      if (row?.visible === false) continue;
      const victims = victimsMap[row.id] ?? [];
      for (const v of victims) {
        const lat = (v as any).lat;
        const lng = (v as any).lng;
        if (typeof lat !== "number" || typeof lng !== "number") continue;
        out.push({
          position: [lat, lng],
          color: row.color,
          kind: kindFromStatus((v as any).status),
          label: victimLabel(v),
          payload: v,
          rowId: row.id,
        });
      }
    }
    if (customCtx) {
      for (const group of customCtx.groups) {
        if (group.visible === false) continue;
        for (const marker of group.markers) {
          const lat = parseFloat(marker.latitude);
          const lng = parseFloat(marker.longitude);
          if (isNaN(lat) || isNaN(lng)) continue;
          out.push({
            position: [lat, lng],
            color: group.color,
            kind: "custom",
            label: marker.name || undefined,
            payload: marker,
            rowId: -(group.id + 1),
          });
        }
      }
    }

    return out;
  }, [ctx.filters, ctx.rows, ctx.victimsByFilter, ctx.victimsByRow, customCtx]);
}